import React from 'react';
import {
  BellIcon,
  UserGroupIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

function StatCard({
  onClick,
  className,
  iconClassName,
  icon: Icon,
  title,
  value,
  subtitle,
  badge,
  valueClassName,
  titleClassName,
  subtitleClassName,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-3">
          <div className={iconClassName}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          {badge}
        </div>
        <dt className={`text-sm font-medium mb-1 ${titleClassName}`}>
          {title}
        </dt>
        <dd className={`text-3xl font-bold ${valueClassName}`}>
          {value}
        </dd>
        <p className={`mt-2 text-xs ${subtitleClassName}`}>
          {subtitle}
        </p>
      </div>
    </button>
  );
}

function ManagerStatsCards({
  pendingRequests = [],
  pendingLeaves = [],
  pendingExtraHours = [],
  teamMembers = [],
  excludedMembers = [],
  todayAttendanceSummary = { activeToday: [], absenteesTotal: 0 },
  onPendingClick = () => {},
  onTeamClick = () => {},
  onExcludedClick = () => {},
  onActiveTodayClick = () => {},
  onAbsenteesClick = () => {},
}) {
  const totalPending = pendingRequests.length + pendingLeaves.length + pendingExtraHours.length;
  const activeTodayCount = todayAttendanceSummary.activeToday?.length || 0;
  const excludedCount = excludedMembers.length;
  const attendancePercent = Math.round((activeTodayCount / Math.max(teamMembers.length, 1)) * 100);

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 mb-8">
      <StatCard
        onClick={onPendingClick}
        className="group relative bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-orange-200 text-left w-full focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
        iconClassName="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-300"
        icon={BellIcon}
        title="Pending Requests"
        value={totalPending}
        subtitle={totalPending > 0 ? 'Click to review' : 'All caught up!'}
        subtitleClassName="text-orange-600"
        titleClassName="text-orange-900"
        valueClassName="text-orange-700"
        badge={totalPending > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-600 text-white animate-pulse">
            New
          </span>
        )}
      />

      <StatCard
        onClick={onTeamClick}
        className="group relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-blue-200 text-left w-full focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
        iconClassName="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-300"
        icon={UserGroupIcon}
        title="Team Members"
        value={teamMembers.length + excludedCount}
        subtitle={excludedCount > 0 ? `${excludedCount} excluded from stats` : 'Click to view team'}
        subtitleClassName="text-blue-600"
        titleClassName="text-blue-900"
        valueClassName="text-blue-700"
      />

      <StatCard
        onClick={onExcludedClick}
        className="group relative bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-amber-200 text-left w-full focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
        iconClassName="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-300"
        icon={ExclamationTriangleIcon}
        title="Excluded"
        value={excludedCount}
        subtitle="Not counted in stats"
        subtitleClassName="text-amber-700"
        titleClassName="text-amber-900"
        valueClassName="text-amber-800"
      />

      <StatCard
        onClick={onActiveTodayClick}
        className="group relative bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-green-200 text-left w-full focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer"
        iconClassName="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-300"
        icon={ChartBarIcon}
        title="Active Today"
        value={activeTodayCount}
        subtitle={`${attendancePercent}% attendance`}
        subtitleClassName="text-green-600"
        titleClassName="text-green-900"
        valueClassName="text-green-700"
      />

      <StatCard
        onClick={onAbsenteesClick}
        className="group relative bg-gradient-to-br from-rose-50 to-orange-100 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-rose-200 text-left w-full focus:outline-none focus:ring-2 focus:ring-rose-400 cursor-pointer"
        iconClassName="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-500 to-orange-500 rounded-lg shadow-lg group-hover:scale-110 transition-transform duration-300"
        icon={UserGroupIcon}
        title="Absentees"
        value={todayAttendanceSummary.absenteesTotal}
        subtitle="On leave + absent today"
        subtitleClassName="text-rose-600"
        titleClassName="text-rose-900"
        valueClassName="text-rose-700"
      />
    </div>
  );
}

export default ManagerStatsCards;
